/**
 * Step 5 — the last look before anything goes out, and where everything that
 * applies to the whole batch is set.
 *
 * Two rules shape it. The count shown is the rows that will actually send, not
 * the rows read. And anything belonging to a *role* — delivery, security —
 * lives on step 3, because changing it here would quietly change which columns
 * the recipient list has to carry.
 */

import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { bulkShellStyles, reviewCardStyles } from "../BulkSend.styles";
import tokens from "../BulkSend.tokens";
import {
  BULK_MESSAGE_MAX,
  BULK_SUBJECT_MAX,
  BulkBatchSettings,
  BulkRoleAssignment,
} from "../BulkSendTypesAndValidation";
import { deliverySummaryFor, mergePreview, roleColor } from "../BulkSendHelper";
import useBulkGates from "../useBulkGates";
import EmailPreviewComponent from "screens/SignRequest/components/EmailPreviewComponent";
import moment from "moment";
import { ButtonBase, InputBase, Tooltip } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailConfigSection from "screens/SignRequest/components/EmailConfigSection";
import EmailConfigSwitch from "screens/SignRequest/components/EmailConfigSwitch";
import emailConfigurationsStyles from "screens/SignRequest/EmailConfigurations.styles";
import emailConfigurationsTokens from "screens/SignRequest/EmailConfigurations.tokens";
import {
  CalendarGlyph,
  CalendarSmallGlyph,
  ClockGlyph,
  DocumentGlyph,
  MailGlyph,
  ShieldCheckGlyph,
} from "screens/SignRequest/components/EmailConfigIcons";
import { ShieldCheckIcon } from "./BulkSendIcons";

const p = tokens.palette;
const c = tokens.reviewCards;
/** The settings cards are the create-request flow's own, styles included. */
const e = emailConfigurationsStyles;
const { lock, schedule } = emailConfigurationsTokens;
/** Same lock mark that flow puts beside a gated control. */
const LOCK_SX = { fontSize: lock.size, color: lock.color };
const SCHEDULE_ICON_SX = {
  width: schedule.pickedIconSize,
  height: schedule.pickedIconSize,
  color: schedule.pickedIconColor,
  flexShrink: 0,
};
const r = reviewCardStyles;

type OpenSection = "email" | "reminders" | "permissions" | "schedule" | "name";

export interface BulkPreviewRow {
  name: string;
  email: string;
  phone?: string;
  /** Column values for the row, keyed by the label a token would name. */
  values: Record<string, string>;
}

interface Props {
  /** The template the batch is built from — its pages, fields and roles. */
  template: any;
  assignments: BulkRoleAssignment[];
  validRows: number;
  settings: BulkBatchSettings;
  /** The first row that will send, for the preview. */
  previewRow?: BulkPreviewRow;
  senderName: string;
  senderEmail: string;
  onChange: (settings: Partial<BulkBatchSettings>) => void;
}

/** Word for word the create-request flow's own signer permissions. */
const BULK_PERMISSIONS: {
  key: keyof BulkBatchSettings["permissions"];
  label: string;
  desc: string;
}[] = [
  {
    key: "allowDocumentDownload",
    label: "Download documents",
    desc: "Signers can download the document",
  },
  {
    key: "sendNotificationOnCompletion",
    label: "Completion notification",
    desc: "Email signers when the request completes",
  },
  {
    key: "attachDocumentToMailOnCompletion",
    label: "Attach signed copies",
    desc: "Include signed documents in the completion email",
  },
  {
    key: "allowSignatureDelegation",
    label: "Delegation",
    desc: "Signers can delegate signing to someone else",
  },
];

const BulkReviewStep: React.FC<Props> = ({
  template,
  assignments,
  validRows,
  settings,
  previewRow,
  senderName,
  senderEmail,
  onChange,
}) => {
  /* Every batch-wide setting is gated as the single-send flow gates it. */
  const gates = useBulkGates();
  const [open, setOpen] = useState<OpenSection | null>("email");

  /**
   * A `SignRequest`-shaped stand-in for the create-request flow's own preview,
   * built from the first row that will actually send. Its merge tokens are
   * resolved here, so the panel shows the mail one real recipient receives
   * rather than the template the batch stores.
   */
  const previewRequest = useMemo(
    () =>
      ({
        emailSubject: mergePreview(
          settings.emailSubject,
          previewRow?.values ?? {},
        ),
        emailMessage: mergePreview(
          settings.emailMessage,
          previewRow?.values ?? {},
        ),
        signers: assignments.map((assignment) => ({
          id: assignment.slot.signerIndex,
          cc: Boolean(assignment.slot.cc),
          name:
            assignment.mode === "vary"
              ? (previewRow?.name ?? "")
              : assignment.name,
          email:
            assignment.mode === "vary"
              ? (previewRow?.email ?? "")
              : assignment.email,
        })),
      }) as any,
    [settings.emailSubject, settings.emailMessage, assignments, previewRow],
  );

  const toggle = (section: OpenSection) =>
    setOpen((current) => (current === section ? null : section));

  const remindersChip = settings.reminders.needReminders
    ? `Every ${settings.reminders.daysBetweenReminders} day(s) · expires in ${settings.reminders.expirationDays}`
    : `No reminders · expires in ${settings.reminders.expirationDays}`;

  const permissionsOn = Object.values(settings.permissions).filter(
    Boolean,
  ).length;

  const expiryNote = `Expires on ${moment()
    .add(Number(settings.reminders.expirationDays) || 0, "days")
    .format("MMMM D, YYYY")}`;

  /** Picking "Schedule for later" needs a time to start from: the next hour. */
  const defaultSendAt = moment()
    .add(1, "hour")
    .startOf("hour")
    .format("YYYY-MM-DDTHH:mm");

  return (
    <Box sx={bulkShellStyles.stepPage("review")}>
      <Typography component="h1" sx={bulkShellStyles.pageTitle}>
        Review &amp; send
      </Typography>
      <Typography component="p" sx={bulkShellStyles.pageSub}>
        Set delivery, reminders and signer permissions, and preview the email
        every recipient receives.
      </Typography>

      <Box sx={e.layout}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={r.factCard}>
            <Box sx={r.factRow}>
              {/*
                The headline number, and the one the Send button acts on — it
                had nowhere to live once the right column became the preview.
              */}
              <Box sx={{ minWidth: 0 }}>
                <Typography component="span" sx={r.factLabel}>
                  Requests
                </Typography>
                <Typography component="span" sx={r.factValue}>
                  {validRows}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography component="span" sx={r.factLabel}>
                  Signers per copy
                </Typography>
                <Typography component="span" sx={r.factValue}>
                  {assignments.length}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography component="span" sx={r.factLabel}>
                  Delivery
                </Typography>
                <Typography component="span" sx={r.factValue}>
                  {deliverySummaryFor(assignments)}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography component="span" sx={r.factLabel}>
                  First reminder
                </Typography>
                <Typography component="span" sx={r.factValue}>
                  {settings.reminders.needReminders
                    ? `In ${settings.reminders.daysBeforeFirstReminder} days`
                    : "Off"}
                </Typography>
              </Box>
            </Box>

            <Box sx={r.factDivider} />

            <Box sx={r.factRoles}>
              <Typography component="span" sx={r.factLabel}>
                Roles
              </Typography>
              {assignments.map((assignment) => (
                <Box
                  key={assignment.slot.signerIndex}
                  component="span"
                  sx={r.roleChip(roleColor(assignment.slot.signerIndex))}
                >
                  {assignment.slot.label} ·{" "}
                  {assignment.mode === "vary" ? "varies" : "fixed"}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ mt: c.sectionGap }}>
            <Box sx={e.sections}>
              <EmailConfigSection
                icon={MailGlyph}
                title="Email content"
                subtitle="Subject and personal message sent to every recipient"
                badge={
                  settings.emailSubject.trim() ? undefined : "Subject required"
                }
                open={open === "email"}
                onToggle={() => toggle("email")}
              >
                <Box sx={e.field}>
                  <Box sx={e.fieldLabelRow}>
                    <Typography component="span" sx={e.fieldLabel}>
                      Email subject{" "}
                      <Box component="span" sx={e.fieldRequired}>
                        *
                      </Box>
                    </Typography>
                    <Typography component="span" sx={e.fieldCounter}>
                      {settings.emailSubject.length}/{BULK_SUBJECT_MAX}
                    </Typography>
                  </Box>
                  <InputBase
                    id="bulk-email-subject"
                    name="emailSubject"
                    value={settings.emailSubject}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      onChange({ emailSubject: event.target.value })
                    }
                    placeholder="Subject your recipients will see"
                    inputProps={{
                      maxLength: BULK_SUBJECT_MAX,
                      "aria-label": "Email subject",
                      "aria-required": true,
                    }}
                    sx={e.subjectInput(!settings.emailSubject.trim())}
                  />
                </Box>

                <Box sx={e.field}>
                  <Box sx={e.fieldLabelRow}>
                    <Typography component="span" sx={e.fieldLabel}>
                      Email message
                    </Typography>
                    <Typography component="span" sx={e.fieldCounter}>
                      {settings.emailMessage.length}/{BULK_MESSAGE_MAX}
                    </Typography>
                  </Box>
                  <InputBase
                    id="bulk-email-message"
                    name="emailMessage"
                    value={settings.emailMessage}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      onChange({ emailMessage: event.target.value })
                    }
                    placeholder="Add a personal note to your recipients (optional)"
                    multiline
                    minRows={4}
                    inputProps={{
                      maxLength: BULK_MESSAGE_MAX,
                      "aria-label": "Email message",
                    }}
                    sx={e.messageInput}
                  />
                </Box>
              </EmailConfigSection>

              <EmailConfigSection
                icon={ClockGlyph}
                title="Reminders & expiration"
                subtitle="Nudge recipients automatically and set a deadline"
                badge={remindersChip}
                open={open === "reminders"}
                onToggle={() => toggle("reminders")}
              >
                <Box sx={e.settingRow}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      component="span"
                      sx={{ ...e.settingLabel(), display: "block" }}
                    >
                      Automatic reminders
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ ...e.settingDesc, display: "block" }}
                    >
                      Email recipients who have not signed yet
                    </Typography>
                  </Box>
                  <Tooltip
                    describeChild
                    title={gates.reminders.tooltip}
                    disableHoverListener={gates.reminders.available}
                    arrow
                  >
                    <Box component="span" sx={{ display: "flex", gap: "8px" }}>
                      {!gates.reminders.available && (
                        <LockOutlinedIcon sx={LOCK_SX} />
                      )}
                      <EmailConfigSwitch
                        id="bulk-need-reminders"
                        label="Automatic reminders"
                        checked={settings.reminders.needReminders}
                        disabled={!gates.reminders.available}
                        onChange={(checked) =>
                          onChange({
                            reminders: {
                              ...settings.reminders,
                              needReminders: checked,
                            },
                          })
                        }
                      />
                    </Box>
                  </Tooltip>
                </Box>

                {settings.reminders.needReminders && (
                  <Box sx={e.nestedPanel}>
                    <Box sx={e.nestedRow}>
                      <Typography component="span" sx={e.nestedLabel()}>
                        First reminder after
                      </Typography>
                      <InputBase
                        id="bulk-first-reminder"
                        value={settings.reminders.daysBeforeFirstReminder}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) =>
                          onChange({
                            reminders: {
                              ...settings.reminders,
                              daysBeforeFirstReminder:
                                Number(event.target.value) || 1,
                            },
                          })
                        }
                        inputProps={{
                          maxLength: 3,
                          inputMode: "numeric",
                          "aria-label": "First reminder after (days)",
                        }}
                        sx={e.numberInput(false, false)}
                      />
                      <Typography component="span" sx={e.unit}>
                        day(s)
                      </Typography>
                    </Box>
                    <Box sx={e.nestedRow}>
                      <Typography component="span" sx={e.nestedLabel()}>
                        Follow-up reminders every
                      </Typography>
                      <InputBase
                        id="bulk-reminder-frequency"
                        value={settings.reminders.daysBetweenReminders}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) =>
                          onChange({
                            reminders: {
                              ...settings.reminders,
                              daysBetweenReminders:
                                Number(event.target.value) || 1,
                            },
                          })
                        }
                        inputProps={{
                          maxLength: 3,
                          inputMode: "numeric",
                          "aria-label": "Follow-up reminders every (days)",
                        }}
                        sx={e.numberInput(false, false)}
                      />
                      <Typography component="span" sx={e.unit}>
                        day(s)
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={e.divider} />

                <Tooltip
                  describeChild
                  title={gates.autoExpiration.tooltip}
                  disableHoverListener={gates.autoExpiration.available}
                  arrow
                >
                  <Box sx={e.settingRow}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        component="span"
                        sx={{
                          ...e.settingLabel(!gates.autoExpiration.available),
                          display: "block",
                        }}
                      >
                        Requests expire after
                      </Typography>
                      <Typography
                        component="span"
                        sx={{ ...e.settingDesc, display: "block" }}
                      >
                        {expiryNote}
                      </Typography>
                    </Box>
                    {!gates.autoExpiration.available && (
                      <LockOutlinedIcon sx={LOCK_SX} />
                    )}
                    <InputBase
                      id="bulk-expiry-days"
                      value={settings.reminders.expirationDays}
                      disabled={!gates.autoExpiration.available}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        onChange({
                          reminders: {
                            ...settings.reminders,
                            expirationDays: Number(event.target.value) || 1,
                          },
                        })
                      }
                      inputProps={{
                        maxLength: 3,
                        inputMode: "numeric",
                        "aria-label": "Requests expire after (days)",
                      }}
                      sx={e.numberInput(true, false)}
                    />
                    <Typography component="span" sx={e.unit}>
                      day(s)
                    </Typography>
                  </Box>
                </Tooltip>
              </EmailConfigSection>

              <EmailConfigSection
                icon={ShieldCheckGlyph}
                title="Signer permissions"
                subtitle="What recipients can do with their copy"
                badge={`${permissionsOn} of ${BULK_PERMISSIONS.length} enabled`}
                open={open === "permissions"}
                onToggle={() => toggle("permissions")}
                tight
              >
                {BULK_PERMISSIONS.map((permission, index) => {
                  /* Delegation is a plan feature; the other three are not. */
                  const locked =
                    permission.key === "allowSignatureDelegation" &&
                    !gates.delegation.available;
                  return (
                    <Box
                      key={permission.key}
                      sx={e.optionRow(index === BULK_PERMISSIONS.length - 1)}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          component="span"
                          sx={{ ...e.settingLabel(locked), display: "block" }}
                        >
                          {permission.label}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{ ...e.settingDesc, display: "block" }}
                        >
                          {permission.desc}
                        </Typography>
                      </Box>
                      <Tooltip
                        describeChild
                        title={locked ? gates.delegation.tooltip : ""}
                        disableHoverListener={!locked}
                        arrow
                      >
                        <Box
                          component="span"
                          sx={{ display: "flex", gap: "8px" }}
                        >
                          {locked && <LockOutlinedIcon sx={LOCK_SX} />}
                          <EmailConfigSwitch
                            id={permission.key}
                            label={permission.label}
                            checked={settings.permissions[permission.key]}
                            disabled={locked}
                            onChange={(checked) =>
                              onChange({
                                permissions: {
                                  ...settings.permissions,
                                  [permission.key]: checked,
                                },
                              })
                            }
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                  );
                })}
              </EmailConfigSection>

              <EmailConfigSection
                icon={CalendarGlyph}
                title="Schedule bulk send"
                subtitle="Send now or pick a later date"
                badge={
                  settings.scheduleTime ? "Scheduled" : "Sends immediately"
                }
                open={open === "schedule"}
                onToggle={() => toggle("schedule")}
              >
                <Typography component="div" sx={e.scheduleIntro}>
                  Send now, or schedule this batch to go out later. Reminders
                  and expiration count from the send time.
                </Typography>

                <Box
                  sx={e.scheduleCards}
                  role="group"
                  aria-label="Choose when to send"
                >
                  <ButtonBase
                    disableRipple
                    onClick={() => onChange({ scheduleTime: "" })}
                    aria-pressed={!settings.scheduleTime}
                    sx={e.scheduleCard(!settings.scheduleTime)}
                  >
                    <Box sx={e.scheduleRadio(!settings.scheduleTime)}>
                      <Box sx={e.scheduleRadioDot(!settings.scheduleTime)} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        component="span"
                        sx={{ ...e.scheduleCardLabel, display: "block" }}
                      >
                        Send immediately
                      </Typography>
                      <Typography
                        component="span"
                        sx={{ ...e.scheduleCardSub, display: "block" }}
                      >
                        As soon as you hit Send
                      </Typography>
                    </Box>
                  </ButtonBase>

                  <Tooltip
                    describeChild
                    title={gates.scheduledSend.tooltip}
                    disableHoverListener={gates.scheduledSend.available}
                    arrow
                  >
                    <Box component="span" sx={{ flex: 1, display: "flex" }}>
                      <ButtonBase
                        disableRipple
                        onClick={() =>
                          onChange({ scheduleTime: defaultSendAt })
                        }
                        aria-pressed={Boolean(settings.scheduleTime)}
                        disabled={!gates.scheduledSend.available}
                        sx={e.scheduleCard(Boolean(settings.scheduleTime))}
                      >
                        <Box
                          sx={e.scheduleRadio(Boolean(settings.scheduleTime))}
                        >
                          <Box
                            sx={e.scheduleRadioDot(
                              Boolean(settings.scheduleTime),
                            )}
                          />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            component="span"
                            sx={{ ...e.scheduleCardLabel, display: "block" }}
                          >
                            Schedule for later
                          </Typography>
                          <Typography
                            component="span"
                            sx={{ ...e.scheduleCardSub, display: "block" }}
                          >
                            Pick a date and time
                          </Typography>
                        </Box>
                        {!gates.scheduledSend.available && (
                          <LockOutlinedIcon sx={{ ...LOCK_SX, ml: "auto" }} />
                        )}
                      </ButtonBase>
                    </Box>
                  </Tooltip>
                </Box>

                {!!settings.scheduleTime && (
                  <Box sx={e.schedulePicked}>
                    <CalendarSmallGlyph sx={SCHEDULE_ICON_SX} />
                    <InputBase
                      id="bulk-schedule-at"
                      type="datetime-local"
                      value={settings.scheduleTime}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        onChange({ scheduleTime: event.target.value })
                      }
                      inputProps={{ "aria-label": "When to start sending" }}
                      sx={e.schedulePickedField}
                    />
                    <Typography component="span" sx={e.schedulePickedNote}>
                      Your local time
                    </Typography>
                  </Box>
                )}
              </EmailConfigSection>

              <EmailConfigSection
                icon={DocumentGlyph}
                title="Batch name & tracking"
                subtitle="Internal label — recipients never see it"
                badge={settings.batchName || "Not set"}
                open={open === "name"}
                onToggle={() => toggle("name")}
              >
                <Box sx={e.field}>
                  <Box sx={e.fieldLabelRow}>
                    <Typography component="span" sx={e.fieldLabel}>
                      Batch name
                    </Typography>
                  </Box>
                  <InputBase
                    id="bulk-batch-name"
                    value={settings.batchName}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      onChange({ batchName: event.target.value })
                    }
                    placeholder="e.g. Employee NDA — Q1 intake"
                    inputProps={{ "aria-label": "Batch name" }}
                    sx={e.subjectInput(false)}
                  />
                </Box>
              </EmailConfigSection>
            </Box>
          </Box>

          <Box sx={r.crossRef}>
            <ShieldCheckIcon size={15} color={p.body} />
            <Box component="span">
              Security and delivery are set per role on the <b>Signers</b> step.
              Change them there, so the columns your recipient list needs stay
              in sync.
            </Box>
          </Box>
        </Box>

        <EmailPreviewComponent signRequest={previewRequest} />
      </Box>
    </Box>
  );
};

export default BulkReviewStep;
